import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST, queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import constate from "constate";
import {
  addMinutes,
  endOfDay,
  format,
  parseISO,
  startOfDay,
  subMinutes,
} from "date-fns";
import {
  OploggForm,
  SubmitTodoForm,
} from "features/TodoOploggSidebar/formSchema";
import {
  DropsLogEntry,
  DropsLogResponse,
} from "features/TodoOploggSidebar/Oplogg/OploggElement/types";
import { upsertPages } from "features/TodoOploggSidebar/Oplogg/OploggElement/utils";
import { useCallback, useState } from "react";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  DropsLogMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

export const dropsLogQueryKey = (
  startDateTime: string | null = null,
  endDateTime: string | null = null,
  searchString: string = "",
  filterBaneNOR: boolean = false,
) => ["dropsLog", startDateTime, endDateTime, searchString, filterBaneNOR];

const upsertMessageToPages = (newMessage: DropsLogEntry) => {
  queryClient.setQueryData<InfiniteData<DropsLogResponse> | undefined>(
    dropsLogQueryKey(),
    (prev) =>
      prev && {
        ...prev,
        pages: upsertPages(prev.pages, newMessage),
      },
  );
};

const SEARCH_TIME_DELTA_MINUTES = 35;

type TimeChangeOptions =
  | "startOfDay"
  | "endOfDay"
  | "deltaBefore"
  | "deltaAfter"
  | "none";

function _parseDatetimeString(
  datetimeString: string,
  changeTime: TimeChangeOptions,
) {
  const date = parseISO(datetimeString);
  switch (changeTime) {
    case "startOfDay":
      return startOfDay(date);
    case "endOfDay":
      return endOfDay(date);
    case "deltaAfter":
      return addMinutes(date, SEARCH_TIME_DELTA_MINUTES);
    case "deltaBefore":
      return subMinutes(date, SEARCH_TIME_DELTA_MINUTES);
    case "none":
      return date;
  }
}

function getTimestamp(
  datetimeString: string | null,
  changeTime: TimeChangeOptions,
) {
  if (!datetimeString) return null;
  const date = _parseDatetimeString(datetimeString, changeTime);
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

type ReadReceiptRequest = {
  entryUuid: string;
  role: string;
};

function _useOplogg() {
  const [oploggNotifications, setOploggNotifications] = useState(0);
  const [selectedDateTime, setSelectedDateTime] = useState<string | null>(null);
  const [showRoles, setShowRoles] = useState(false);
  const [showVehicleSets, setShowVehicleSets] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [filterBaneNOR, setFilterBaneNOR] = useState(false);

  const messageHandler = useCallback(
    ({ message }: DropsLogMessage) => {
      setOploggNotifications((prevNotifications) => prevNotifications + 1);
      upsertMessageToPages(message);
    },
    [setOploggNotifications, upsertMessageToPages],
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.DROPS_LOG]: messageHandler,
  });

  const { mutate: post, status: postStatus } = useMutation({
    mutationFn: (data: OploggForm) =>
      mutationFnPOST<DropsLogEntry | null, OploggForm>(
        `${getBackendUrl()}/drops-log/logs`,
        data,
      ),
    onSuccess: (entry) => {
      if (entry) upsertMessageToPages(entry);
    },
  });

  const { mutate: postReceipt, status: postReceiptStatus } = useMutation({
    mutationFn: (data: ReadReceiptRequest) =>
      mutationFnPOST<DropsLogEntry, ReadReceiptRequest>(
        `${getBackendUrl()}/drops-log/logs/read`,
        data,
      ),
    onSuccess: (entry) => {
      if (entry) upsertMessageToPages(entry);
    },
  });

  const { mutate: postToDo } = useMutation({
    mutationFn: (data: SubmitTodoForm) =>
      mutationFnPOST<undefined, SubmitTodoForm>(
        `${getBackendUrl()}/todo`,
        data,
      ),
  });

  const paginationLimit = selectedDateTime || searchString ? 5000 : 20;

  const searchFullDay = !selectedDateTime?.includes("T") || !!searchString;
  const startDateTime = getTimestamp(
    selectedDateTime,
    searchFullDay ? "startOfDay" : "deltaBefore",
  );
  const endDateTime = getTimestamp(
    selectedDateTime,
    searchFullDay ? "endOfDay" : "deltaAfter",
  );

  const { data, hasNextPage, status, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: dropsLogQueryKey(
        startDateTime,
        endDateTime,
        searchString,
        filterBaneNOR,
      ),
      initialPageParam: 0,
      queryFn: ({ signal, pageParam }) =>
        queryFnGET<DropsLogResponse>({
          signal,
          url: `${getBackendUrl()}/drops-log/logs?pageSize=${encodeURIComponent(paginationLimit)}${
            pageParam > 0 ? `&pageOffset=${encodeURIComponent(pageParam)}` : ""
          }${startDateTime ? `&minDateTime=${encodeURIComponent(startDateTime)}` : ""}${
            endDateTime ? `&maxDateTime=${encodeURIComponent(endDateTime)}` : ""
          }${searchString ? `&searchString=${encodeURIComponent(searchString)}` : ""}${filterBaneNOR ? `&filterBaneNOR=true` : ""}`,
        }),
      getNextPageParam: (lastPage) =>
        lastPage.hasNext ? lastPage.lastSeen : null,
      refetchOnWindowFocus: true,
    });

  return {
    post,
    postStatus,
    postToDo,
    postReceipt,
    postReceiptStatus,
    setSelectedDateTime,
    data,
    status,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    showRoles,
    setShowRoles,
    showVehicleSets,
    setShowVehicleSets,
    oploggNotifications, // used alongside todoNotifications so oplogg is specified in name
    setOploggNotifications,
    setSearchString,
    searchString,
    setFilterBaneNOR,
    filterBaneNOR,
  };
}

export const [OploggProvider, useOplogg] = constate(_useOplogg);
